import type { LeagueSummary } from "@softmax/observatory-client/types";
import { featuredCoworlds } from "@softmax/web-features/site/records";
import {
  catalogListedLeagues,
  curatedCoworldName,
  isCuratedCoworld,
  curatedLeagueName,
  curatedText,
} from "@softmax/web-features/coworlds";
import { buildHref, type GameDestinationContext } from "@softmax/web-addresses";
import { leagueRoute, slugifyLeagueName } from "@softmax/web-features/leagues";
import { curatedThumb } from "@softmax/web-features/replay/art";

export type CoworldRow = {
  key: string;
  name: string;
  path: string;
  route: GameDestinationContext;
  corpusSlug: string;
  description: string | null;
  tags: string[];
  gameOfWeek: boolean;
  leagues: [{ id: string; name: string }, ...{ id: string; name: string }[]];
  thumb: string | null;
};

export function listedBrowseRows(leagues: LeagueSummary[]): CoworldRow[] {
  const groups = new Map<string, [LeagueSummary, ...LeagueSummary[]]>();

  for (const league of catalogListedLeagues(leagues)) {
    const group = groups.get(league.game.id);

    if (group) group.push(league);
    else groups.set(league.game.id, [league]);
  }

  const rows = [...groups.values()]
    .map((group): CoworldRow => {
      const game = group[0].game;
      const alphabetical: [LeagueSummary, ...LeagueSummary[]] = [...group];
      alphabetical.sort((a, b) => a.name.localeCompare(b.name));
      const front =
        group.find((league) => league.id === game.default_league_id) ??
        alphabetical[0];
      const route = { game: leagueRoute(front).game };
      const corpusSlug = slugifyLeagueName(game.coworld_name ?? game.name);
      return {
        key: game.id,
        name: curatedCoworldName(game.coworld_name ?? game.name) ?? game.name,
        path: buildHref({ name: "game.home", ...route }),
        route,
        corpusSlug,
        thumb: curatedThumb(corpusSlug),
        description: game.description?.trim()
          ? curatedText(game.description.trim())
          : null,
        tags: game.coworld_tags ?? [],
        gameOfWeek: group.some((league) => Boolean(league.is_game_of_week)),
        leagues: [
          { id: front.id, name: curatedLeagueName(front) },
          ...alphabetical
            .filter((league) => league !== front)
            .map((league) => ({
              id: league.id,
              name: curatedLeagueName(league),
            })),
        ],
      };
    })
    .toSorted((a, b) => a.name.localeCompare(b.name));

  const pinned = rows.filter(
    (row) => row.gameOfWeek && isCuratedCoworld(row.corpusSlug),
  );
  const featured = rows
    .filter(
      (row) =>
        !pinned.includes(row) && featuredCoworlds.includes(row.corpusSlug),
    )
    .toSorted(
      (a, b) =>
        featuredCoworlds.indexOf(a.corpusSlug) -
        featuredCoworlds.indexOf(b.corpusSlug),
    );
  const other = rows.filter(
    (row) => !pinned.includes(row) && !featured.includes(row),
  );
  return [...featured.slice(0, 1), ...pinned, ...featured.slice(1), ...other];
}
